import { useCallback } from 'react';
import useDatabase from '@pkg/database/useDatabase';
import useMutation from '@pkg/graphql/useMutation';
import useStore from '../useStore';

const CREATE_ACTIVITY = /* GraphQL */ `
  mutation CreateLibraryActivity($input: CreateLibraryActivityInput!) {
    createLibraryActivity(input: $input) {
      uuid
      description
    }
  }
`;

export default function useCreate() {
  const database = useDatabase();
  const { add, remove, getExisting } = useStore();
  const { mutateAsync } = useMutation({ mutation: CREATE_ACTIVITY });

  /**
   * @param {String} uuid
   * @param {String} description
   * @returns {Promise<String|null>} the effective library activity uuid, or
   *   null if the create failed.
   */
  async function createActivity(uuid, description) {
    // Reuse an existing activity rather than attempting a duplicate create.
    const existingId = getExisting(description);
    if (existingId) {
      return existingId;
    }

    const input = { uuid, description };
    add(input);
    await database.activities.put(input);

    const result = await mutateAsync(input);

    // on error remove the invalid uuid
    if (result?.errors?.length || !result?.data?.createLibraryActivity) {
      remove(uuid);
      await database.activities.delete(uuid);
      return null;
    }

    return result.data.createLibraryActivity.uuid;
  }

  return useCallback(createActivity, [mutateAsync]);
}
